import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, FlaskConical, Atom, Microscope } from "lucide-react";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";

const COMP = [
    { prop: "Flexural Strength (MPa)", tr: 480, zr: 1100, pfm: 350, note: "Sufficient for posterior occlusal load" },
    { prop: "Wear on opposing dentition", tr: "Enamel-like", zr: "High abrasion", pfm: "Variable", note: "Critical for natural opposing teeth" },
    { prop: "Aesthetic translucency", tr: "High", zr: "Low–Med", pfm: "Low (metal core)", note: "Native enamel mimicry" },
    { prop: "Marginal fit precision", tr: "<25 µm", zr: "30–80 µm", pfm: "60–100 µm", note: "Direct print, no firing shrinkage" },
    { prop: "Bond mechanism", tr: "Adhesive (silane + resin cement)", zr: "MDP-based", pfm: "Mechanical / luting", note: "" },
    { prop: "Repairable chairside", tr: "Yes", zr: "No", pfm: "Limited", note: "Fracture / chip repairable in-office" },
];

const FAQ = [
    {
        q: "How does the Nano Hybrid Resin bond clinically?",
        a: "After try-in, the intaglio surface is sandblasted (50 µm Al₂O₃ at 2 bar), silane-coated, and bonded with a dual-cure resin cement such as RelyX Ultimate or Panavia V5. We supply a single-page bonding protocol with every dispatch.",
    },
    {
        q: "Which cements are recommended?",
        a: "Dual-cure resin cements (RelyX Ultimate, Panavia V5, Variolink Esthetic DC) are the protocol cement. Self-adhesive cements are acceptable for posterior crowns where isolation is challenging.",
    },
    {
        q: "How does it perform under bruxism?",
        a: "Long-term cohort data shows acceptable performance with a night guard. We do not recommend the material as a standalone restoration in severe parafunction without occlusal protection.",
    },
    {
        q: "Is the material radiopaque on bitewings?",
        a: "Yes. The nano-ceramic filler renders the restoration radiographically distinguishable from natural dentine, supporting follow-up diagnostics.",
    },
    {
        q: "What surface finishing do you ship with?",
        a: "Each unit ships glaze-fired to a clinical polish; minor occlusal adjustments can be re-polished chairside with standard composite polishing kits.",
    },
    {
        q: "How does it integrate with my scanner?",
        a: "We accept STL, PLY, OBJ, and DCM exports from 3Shape TRIOS, Medit, iTero, Primescan, Carestream, and Shining 3D. Direct cloud transfer is available for 3Shape Communicate and Medit Link.",
    },
];

export default function Materials() {
    return (
        <div data-testid="materials-page" className="bg-white">
            {/* HERO */}
            <section className="relative overflow-hidden bg-[#0B1120] text-white tr-grain">
                <div className="absolute -top-32 right-1/4 w-[500px] h-[500px] tr-glow bg-cyan-500/30" />
                <div className="absolute bottom-0 -left-32 w-[420px] h-[420px] tr-glow bg-teal-500/25" />
                <div className="relative max-w-[1280px] mx-auto px-5 lg:px-8 py-24 lg:py-32 grid lg:grid-cols-12 gap-10 items-end">
                    <div className="lg:col-span-8">
                        <div className="tr-overline text-cyan-400 mb-4">· Materials & technology</div>
                        <h1 className="font-display font-light text-5xl lg:text-7xl tracking-tight leading-[0.98]">
                            The science of<br />
                            <span className="italic font-extralight text-cyan-300/90">better smiles.</span>
                        </h1>
                    </div>
                    <div className="lg:col-span-4">
                        <p className="text-white/65 text-[15px] leading-[1.75]">
                            A purely polymeric-ceramic matrix engineered for additive manufacturing.
                            Below: the chemistry, the clinical numbers, and the protocol.
                        </p>
                    </div>
                </div>
            </section>

            {/* MATERIAL DEEP DIVE */}
            <section className="max-w-[1280px] mx-auto px-5 lg:px-8 py-24" data-testid="material-deep-dive">
                <div className="grid lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-4">
                        <div className="tr-overline mb-3">· Composition</div>
                        <h2 className="font-display font-light text-4xl tracking-tight leading-tight text-slate-900">
                            Nano Ceramic<br />Hybrid Resin.
                        </h2>
                    </div>
                    <div className="lg:col-span-8 grid sm:grid-cols-2 gap-4">
                        {[
                            { i: Atom, t: "Filler load", v: "78% by weight", d: "Sub-50 nm silanated zirconia–silica clusters embedded in a UDMA matrix." },
                            { i: FlaskConical, t: "Polymer matrix", v: "UDMA + Bis-EMA", d: "Low-shrinkage urethane network for additive-printability." },
                            { i: Microscope, t: "Filler size", v: "20–80 nm", d: "Nano-scale clusters maintain optical clarity and polish retention." },
                            { i: ShieldCheck, t: "Biocompatibility", v: "ISO 10993", d: "Tested for cytotoxicity, sensitization, and irritation." },
                        ].map((c) => {
                            const Icon = c.i;
                            return (
                                <div key={c.t} className="tr-card p-6">
                                    <div className="flex items-center justify-between">
                                        <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center text-slate-700">
                                            <Icon size={18} />
                                        </div>
                                        <div className="font-mono-tr text-[10px] tracking-[0.2em] uppercase text-slate-500">{c.t}</div>
                                    </div>
                                    <div className="mt-4 font-display text-2xl text-slate-900 tracking-tight">{c.v}</div>
                                    <p className="mt-1.5 text-[13.5px] text-slate-600 leading-relaxed">{c.d}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* COMPARISON TABLE */}
            <section className="bg-slate-50 border-y border-slate-200" data-testid="comparison-section">
                <div className="max-w-[1280px] mx-auto px-5 lg:px-8 py-24">
                    <div className="grid lg:grid-cols-12 gap-10 mb-12">
                        <div className="lg:col-span-7">
                            <div className="tr-overline mb-4">· Clinical comparison</div>
                            <h2 className="font-display font-light text-4xl sm:text-5xl tracking-tight text-slate-900 leading-tight">
                                Nano Hybrid vs Zirconia vs PFM.
                            </h2>
                        </div>
                        <div className="lg:col-span-4 lg:col-start-9 self-end">
                            <p className="text-slate-600 text-[15px] leading-relaxed">
                                Numbers are derived from internal QC and peer-reviewed literature. We&rsquo;re happy to share full
                                methodology — write to <span className="text-slate-900 font-medium">science@toothrocket.com</span>.
                            </p>
                        </div>
                    </div>

                    <div className="rounded-2xl overflow-hidden border border-slate-200 bg-white">
                        <div className="grid grid-cols-[1.4fr_1fr_1fr_1fr] gap-0 text-[12px] font-mono-tr tracking-[0.18em] uppercase text-slate-500 bg-slate-100 px-6 py-4">
                            <div>Property</div>
                            <div className="text-slate-900">Nano Hybrid (TR)</div>
                            <div>Zirconia</div>
                            <div>PFM</div>
                        </div>
                        {COMP.map((row, i) => (
                            <div
                                key={row.prop}
                                className={`grid grid-cols-[1.4fr_1fr_1fr_1fr] gap-0 px-6 py-5 text-[14.5px] items-start ${
                                    i % 2 === 0 ? "bg-white" : "bg-slate-50/50"
                                } border-t border-slate-100`}
                            >
                                <div className="text-slate-900 font-medium">
                                    {row.prop}
                                    {row.note && <div className="text-[12px] text-slate-500 font-normal mt-1">{row.note}</div>}
                                </div>
                                <div className="text-cyan-700 font-semibold">{row.tr}</div>
                                <div className="text-slate-700">{row.zr}</div>
                                <div className="text-slate-700">{row.pfm}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* WARRANTY DETAILS */}
            <section className="max-w-[1280px] mx-auto px-5 lg:px-8 py-24" data-testid="warranty-details">
                <div className="grid lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-5">
                        <div className="tr-overline mb-3">· The 10-year warranty</div>
                        <h2 className="font-display font-light text-4xl lg:text-5xl tracking-tight leading-[1.05] text-slate-900">
                            Why we can promise<br />
                            <span className="italic">a full decade.</span>
                        </h2>
                        <div className="mt-8 rounded-2xl bg-[#0B1120] text-white p-8">
                            <div className="font-display text-7xl tracking-tighter leading-none">10<span className="text-cyan-400">.</span></div>
                            <div className="mt-2 font-mono-tr text-[11px] tracking-[0.22em] uppercase text-cyan-300">Year clinical warranty</div>
                            <div className="mt-5 text-white/70 text-[14px] leading-relaxed">
                                Direct replacement printed within 12 hours of any covered claim. Auto-generated digital warranty card. Simple. Documented.
                            </div>
                        </div>
                    </div>
                    <div className="lg:col-span-7">
                        <ol className="space-y-7">
                            {[
                                {
                                    n: "01",
                                    t: "Predictable wear curve",
                                    d: "Internal accelerated wear simulators (chewing rig, 1.2M cycles) project a wear of <60 µm at year 10 — well within physiological enamel range.",
                                },
                                {
                                    n: "02",
                                    t: "Repair, don't replace",
                                    d: "Unlike monolithic zirconia, the nano-ceramic surface accepts in-mouth composite repair after micro-chipping, extending unit life without re-prep.",
                                },
                                {
                                    n: "03",
                                    t: "Patient ID tracking",
                                    d: "Every printed unit is linked to a patient record. We can re-print the exact STL on demand — no rescan required.",
                                },
                                {
                                    n: "04",
                                    t: "Bonded interface",
                                    d: "Adhesive cementation creates a continuous tooth–restoration interface, distributing load and reducing the most common failure mode (debonding).",
                                },
                            ].map((s) => (
                                <li key={s.n} className="grid grid-cols-[80px_1fr] gap-4 pb-7 border-b border-slate-100 last:border-0">
                                    <div className="font-display font-mono-tr text-3xl text-slate-300">{s.n}</div>
                                    <div>
                                        <div className="font-display text-xl tracking-tight text-slate-900">{s.t}</div>
                                        <div className="mt-1.5 text-[14.5px] text-slate-600 leading-relaxed">{s.d}</div>
                                    </div>
                                </li>
                            ))}
                        </ol>
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="border-t border-slate-200 bg-slate-50/60" data-testid="faq-section">
                <div className="max-w-[1280px] mx-auto px-5 lg:px-8 py-24 grid lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-4">
                        <div className="tr-overline mb-3">· Technical FAQ</div>
                        <h2 className="font-display font-light text-4xl lg:text-5xl tracking-tight leading-[1.05] text-slate-900">
                            Clinical questions,<br /><span className="italic">honestly answered.</span>
                        </h2>
                    </div>
                    <div className="lg:col-span-8">
                        <Accordion type="single" collapsible className="w-full" defaultValue="item-0">
                            {FAQ.map((f, i) => (
                                <AccordionItem
                                    key={f.q}
                                    value={`item-${i}`}
                                    className="border-b border-slate-200"
                                    data-testid={`faq-item-${i}`}
                                >
                                    <AccordionTrigger className="text-left text-[16px] lg:text-[17px] font-medium text-slate-900 hover:no-underline py-5">
                                        {f.q}
                                    </AccordionTrigger>
                                    <AccordionContent className="text-[14.5px] text-slate-600 leading-relaxed pb-6">
                                        {f.a}
                                    </AccordionContent>
                                </AccordionItem>
                            ))}
                        </Accordion>
                    </div>
                </div>
            </section>

            <section className="max-w-[1280px] mx-auto px-5 lg:px-8 py-24">
                <div className="rounded-3xl border border-slate-200 bg-white p-10 lg:p-14 grid lg:grid-cols-12 gap-6 items-center">
                    <div className="lg:col-span-8">
                        <h3 className="font-display font-light text-4xl tracking-tight text-slate-900 leading-tight">
                            Ready to prescribe a Nano Hybrid Ceramic restoration?
                        </h3>
                    </div>
                    <div className="lg:col-span-4 lg:flex lg:justify-end">
                        <Link to="/workflow#send-case" className="tr-btn">Send a Case <ArrowRight size={16} /></Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
